import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsLinkInline,
  HdsButton,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  CoreModalConfirm,
  Typography,
} from 'core/components';
import { resourceTypeFor } from 'core/helpers';
import { boxPadding } from 'core/modifiers';
import ListTable from 'hcp/components/service-principal/keys/list-table';
import GenerateModal from 'hcp/components/service-principal/keys/generate-modal';

/**
 * Contains the `servicePrincipal` and 'project'.
 * @argument model
 * @type {Object}
 */
/**
 *
 * `PageServicePrincipalsProjectDetailKeys` displays the keys of a project Service Principal and allows for them to be added and/or deleted.
 *
 *
 * ```
 * <Page::ServicePrincipals::Project::Detail::Keys
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageServicePrincipalsProjectDetailKeys
 *
 */
export default class PageServicePrincipalsProjectDetailKeys extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;
  @service quota;

  @tracked showDeleteKeyModal = false;
  @tracked servicePrincipalKeyToDelete = null;

  @tracked showGenerateKeyModal = false;

  @tracked returnFocusTo = null;

  confirmDeleteServicePrincipalKey = (servicePrincipalKey) => {
    this.showDeleteKeyModal = true;
    this.servicePrincipalKeyToDelete = servicePrincipalKey;
  };

  deleteServicePrincipalKey = task(async () => {
    const {
      servicePrincipal: { organizationId, projectId },
    } = this.args.model;
    const response =
      await this.api.servicePrincipal.servicePrincipalsServiceDeleteProjectServicePrincipalKey(
        organizationId,
        projectId,
        this.servicePrincipalKeyToDelete.clientId
      );
    this.onDeleteServicePrincipalKeySuccess({
      response,
      servicePrincipalKey: this.servicePrincipalKeyToDelete,
    });
  });

  onDeleteServicePrincipalKeySuccess = () => {
    this.resetDeleteServicePrincipalKey();
    this.refreshRoute();
    this.flashMessages.success(
      this.intl.t(
        'components.page.access-control.service-principals.delete-key-modal.success'
      )
    );
  };

  resetDeleteServicePrincipalKey = () => {
    this.showDeleteKeyModal = false;
    this.servicePrincipalKeyToDelete = null;
  };

  toggleGenerateKeyModal = () => {
    this.showGenerateKeyModal = !this.showGenerateKeyModal;
  };

  onGenerateKeySuccess = () => {
    this.refreshRoute();
    this.flashMessages.success(
      this.intl.t(
        'components.page.access-control.service-principals.details.actions.generate-key.success'
      )
    );
  };

  refreshRoute() {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  }

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="keychain" />
          <PH.Title>
            {{t
              "components.page.access-control.service-principals.details.keys.header.title"
            }}
          </PH.Title>
          <PH.Description data-test-service-principal-keys-description>
            {{t
              "components.page.access-control.service-principals.details.keys.header.description"
              htmlSafe=true
            }}
            <HdsLinkInline
              @color="secondary"
              @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/service-principal#generate-a-service-principal-key"
            >
              {{t
                "components.page.access-control.service-principals.details.keys.header.action.learn-more"
              }}
            </HdsLinkInline>
          </PH.Description>
          <PH.Actions>
            {{#if (can "create service-principal-key" @model.keys)}}
              <HdsButton
                @icon="plus"
                @text={{t
                  "components.page.access-control.service-principals.details.keys.header.action.generate"
                }}
                {{on "click" this.toggleGenerateKeyModal}}
                data-test-button-generate-key-header
              />
            {{/if}}
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:body>
        <LimitBanner
          @variant={{resourceTypeFor "service-principal-key"}}
          @count={{@model.keys.length}}
        />
        {{#if @model.keys.length}}
          <ListTable
            {{boxPadding "2xl 0 2xl 0"}}
            @keys={{@model.keys}}
            @handleConfirmDeleteKey={{this.confirmDeleteServicePrincipalKey}}
          />
        {{else}}
          <HdsApplicationState {{boxPadding "2xl 0 2xl 0"}} as |A|>
            <A.Header
              data-test-empty-state-header
              @title={{t
                "components.page.access-control.service-principals.details.keys.empty.header"
              }}
            />

            <A.Body
              @text={{t
                "components.page.access-control.service-principals.details.keys.empty.message"
                htmlSafe=true
              }}
            />

            {{#if (can "create service-principal-key")}}
              <A.Footer>
                <HdsButton
                  data-test-button-generate-key
                  @color="tertiary"
                  @icon="plus"
                  @text={{t
                    "components.page.access-control.service-principals.details.keys.empty.action"
                  }}
                  {{on "click" this.toggleGenerateKeyModal}}
                />
              </A.Footer>
            {{/if}}
          </HdsApplicationState>
        {{/if}}
        {{#if this.showDeleteKeyModal}}
          <CoreModalConfirm
            @confirmHelp={{t
              "components.page.access-control.service-principals.delete-key-modal.helper-text"
              htmlSafe=true
            }}
            @confirmText={{t "components.core.modal.confirm.delete"}}
            @headerIcon="alert-diamond"
            @headerText={{t
              "components.page.access-control.service-principals.delete-key-modal.title"
            }}
            @onCancel={{this.resetDeleteServicePrincipalKey}}
            @onSubmit={{this.deleteServicePrincipalKey}}
          >
            <:body>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 lg 0"}}
              >
                {{t
                  "components.page.access-control.service-principals.delete-key-modal.body"
                  servicePrincipalName=@model.servicePrincipal.name
                  htmlSafe=true
                }}
              </Typography>
            </:body>
          </CoreModalConfirm>
        {{/if}}
        {{#if this.showGenerateKeyModal}}
          <GenerateModal
            @model={{@model}}
            @onClose={{this.toggleGenerateKeyModal}}
            @onSuccess={{this.onGenerateKeySuccess}}
          />
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
